import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { Modal, Button, ButtonVariant, ButtonStyle, H3 } from '@meiuca/ui-components'
import { Modality } from '@/types/modality'
import { Amenity } from '@/types/amenity'

interface ArenaFilterModalProps {
  open: boolean
  onClose: () => void
  modalities: Modality[]
  amenities: Amenity[]
  selectedModalityIds: Set<string>
  selectedAmenityIds: Set<string>
  onApplyFilters: (modalityIds: Set<string>, amenityIds: Set<string>) => void
  onClearFilters: () => void
}

export function ArenaFilterModal({
  open,
  onClose,
  modalities,
  amenities,
  selectedModalityIds,
  selectedAmenityIds,
  onApplyFilters,
  onClearFilters,
}: ArenaFilterModalProps) {
  const [tempSelectedModalityIds, setTempSelectedModalityIds] = useState<Set<string>>(selectedModalityIds)
  const [tempSelectedAmenityIds, setTempSelectedAmenityIds] = useState<Set<string>>(selectedAmenityIds)

  useEffect(() => {
    setTempSelectedModalityIds(new Set(selectedModalityIds))
    setTempSelectedAmenityIds(new Set(selectedAmenityIds))
  }, [selectedModalityIds, selectedAmenityIds])

  const toggleModality = (modalityId: string) => {
    const newSet = new Set(tempSelectedModalityIds)
    if (newSet.has(modalityId)) {
      newSet.delete(modalityId)
    } else {
      newSet.add(modalityId)
    }
    setTempSelectedModalityIds(newSet)
  }

  const toggleAmenity = (amenityId: string) => {
    const newSet = new Set(tempSelectedAmenityIds)
    if (newSet.has(amenityId)) {
      newSet.delete(amenityId)
    } else {
      newSet.add(amenityId)
    }
    setTempSelectedAmenityIds(newSet)
  }

  const handleApply = () => {
    onApplyFilters(tempSelectedModalityIds, tempSelectedAmenityIds)
    onClose()
  }

  const handleClear = () => {
    setTempSelectedModalityIds(new Set())
    setTempSelectedAmenityIds(new Set())
    onClearFilters()
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      closeOnOverlayClick={true}
      showCloseButton={false}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-border-light">
          <H3>Filtros</H3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-background-secondary rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-text-tertiary" />
          </button>
        </div>

        {/* Modalities Section */}
        <div>
          <h4 className="font-semibold text-text-primary mb-3">Modalidades</h4>
          <div className="grid grid-cols-2 gap-2">
            {modalities.map((modality) => {
              const isSelected = tempSelectedModalityIds.has(modality.id)
              return (
                <label
                  key={modality.id}
                  className={`
                    flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                    ${
                      isSelected
                        ? 'border-brand-primary bg-brand-primary/5'
                        : 'border-border-light hover:border-brand-primary/50'
                    }
                  `}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleModality(modality.id)}
                    className="w-4 h-4 text-brand-primary rounded border-border-primary focus:ring-brand-primary"
                  />
                  <span className="text-sm text-text-primary">{modality.description}</span>
                </label>
              )
            })}
          </div>

          {modalities.length === 0 && (
            <p className="text-center text-text-secondary py-4">Carregando modalidades...</p>
          )}
        </div>

        {/* Amenities Section */}
        <div>
          <h4 className="font-semibold text-text-primary mb-3">Comodidades</h4>
          <div className="grid grid-cols-2 gap-2">
            {amenities.map((amenity) => {
              const isSelected = tempSelectedAmenityIds.has(amenity.id)
              return (
                <label
                  key={amenity.id}
                  className={`
                    flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                    ${
                      isSelected
                        ? 'border-brand-primary bg-brand-primary/5'
                        : 'border-border-light hover:border-brand-primary/50'
                    }
                  `}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleAmenity(amenity.id)}
                    className="w-4 h-4 text-brand-primary rounded border-border-primary focus:ring-brand-primary"
                  />
                  <span className="text-sm text-text-primary">{amenity.label}</span>
                </label>
              )
            })}
          </div>

          {amenities.length === 0 && (
            <p className="text-center text-text-secondary py-4">Carregando comodidades...</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between gap-3 pt-4 border-t border-border-light">
          <Button
            buttonStyle={ButtonStyle.BORDERED}
            onClick={handleClear}
          >
            Limpar filtros
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            onClick={handleApply}
          >
            Aplicar filtros
          </Button>
        </div>
      </div>
    </Modal>
  )
}
